import { connectorTitles, type OutboxEntry, type OutboxStatus } from '@hugo/core'
import { ExternalLinkIcon, InfoIcon, SendIcon, Trash2Icon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { HeadsUpCard } from '@/components/heads-up'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Kbd } from '@/components/ui/kbd'
import { Skeleton } from '@/components/ui/skeleton'
import { View } from '@/components/view'
import { useWorkspace } from '@/components/workspace'
import { ApiError } from '@/lib/api'
import { formatDateTime, outboxStatusLabel, relativeTime } from '@/lib/format'
import { useHeadsUps, useOutbox, useSettings } from '@/lib/items'
import { isTypingTarget, overlayOpen } from '@/lib/keys'
import { useOutboxActions } from '@/lib/outbox'
import { toSendHeading, userToday } from '@/lib/plan'
import { useUserNow } from '@/lib/session'
import { cn } from '@/lib/utils'

/** Waiting entries are often selected (accent background): theirs needs a border to show. */
const STATUS_VARIANT: Record<OutboxStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  pending: 'outline',
  sending: 'secondary',
  sent: 'default',
  dry_run: 'secondary',
  failed: 'destructive',
  discarded: 'outline',
  withdrawn: 'outline',
}

/**
 * One message hugo would send: who gets it, in which thread, the text, and what happened to it.
 * Waiting entries can be selected (keyboard) and approved or discarded.
 */
function OutboxItem({
  entry,
  now,
  selected = false,
  onSelect,
  onApprove,
  onDiscard,
}: {
  entry: OutboxEntry
  now: Date
  selected?: boolean
  onSelect?: (entry: OutboxEntry) => void
  onApprove?: (entry: OutboxEntry) => void
  onDiscard?: (entry: OutboxEntry) => void
}) {
  const at = entry.sentAt ?? entry.decidedAt ?? entry.createdAt
  const summaryId = `outbox-${entry.id}-summary`
  const summary = (
    <span id={summaryId} className="font-medium break-words">
      {entry.summary}
    </span>
  )
  return (
    <article
      data-outbox-id={entry.id}
      data-status={entry.status}
      data-selected={selected}
      aria-labelledby={summaryId}
      className={cn(
        'flex flex-col gap-3 p-4 transition-colors',
        selected && 'bg-accent ring-2 ring-ring/40 ring-inset',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        {onSelect ? (
          <button
            type="button"
            aria-current={selected || undefined}
            onClick={() => onSelect(entry)}
            className="min-w-0 flex-1 rounded-sm text-left outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
          >
            {summary}
          </button>
        ) : (
          <div className="min-w-0 flex-1">{summary}</div>
        )}
        <time
          dateTime={at}
          title={formatDateTime(new Date(at))}
          className="shrink-0 pt-0.5 text-xs text-muted-foreground"
        >
          {relativeTime(new Date(at), now)}
        </time>
      </div>

      <div className="flex min-w-0 flex-col items-start gap-1.5 text-sm">
        <Badge
          variant={STATUS_VARIANT[entry.status]}
          className="h-auto max-w-full text-left whitespace-normal"
          data-testid="outbox-status"
        >
          {outboxStatusLabel(entry.status, entry.error)}
        </Badge>
        {entry.action === 'set_status' ? null : entry.recipients.length === 0 ? (
          // A comment goes into the thread itself (GitHub), addressed to nobody.
          <p className="max-w-full break-words" data-testid="outbox-to">
            Comment in {connectorTitles[entry.connector]}
          </p>
        ) : (
          <p className="max-w-full break-words" data-testid="outbox-to">
            <span className="text-muted-foreground">To: </span>
            {entry.recipients.join(', ')}
          </p>
        )}
        {entry.thread ? (
          <p className="flex max-w-full flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="min-w-0 break-words text-muted-foreground" data-testid="outbox-thread">
              {entry.thread.subject}
            </span>
            {entry.thread.url ? (
              <a
                href={entry.thread.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 font-medium underline-offset-4 hover:underline"
              >
                Open in {connectorTitles[entry.connector]}
                <ExternalLinkIcon aria-hidden="true" className="size-3.5" />
              </a>
            ) : null}
          </p>
        ) : null}
      </div>

      {/* A status change (decision 108) has no message: its summary says it all. */}
      {entry.action === 'set_status' ? null : (
        <p className="rounded-lg border bg-background p-3 text-sm break-words whitespace-pre-wrap">
          {entry.body}
        </p>
      )}

      {onApprove && onDiscard ? (
        // Phones: full-width buttons, the primary one on top (thumb reach). Desktop: one row.
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            variant="outline"
            className="h-10 w-full sm:h-8 sm:w-auto"
            aria-keyshortcuts="x"
            onClick={() => onDiscard(entry)}
          >
            <Trash2Icon aria-hidden="true" />
            Discard
            <Kbd aria-hidden="true" className="max-md:hidden">
              X
            </Kbd>
          </Button>
          <Button
            className="h-10 w-full sm:h-8 sm:w-auto"
            aria-keyshortcuts="a"
            onClick={() => onApprove(entry)}
          >
            <SendIcon aria-hidden="true" />
            Approve and send
            <Kbd
              aria-hidden="true"
              className="bg-primary-foreground/15 text-primary-foreground max-md:hidden"
            >
              A
            </Kbd>
          </Button>
        </div>
      ) : null}
    </article>
  )
}

/**
 * People who should hear from the user (heads-ups and reminders to send), everything hugo would
 * send, waiting for approval, plus what was decided recently (sent, discarded, withdrawn by hugo).
 * Keyboard like the lists for the drafts: j/k move, a approves, x discards, c captures; heads-ups
 * are reached with Tab.
 */
export function OutboxPage() {
  const pendingQuery = useOutbox('pending')
  const recentQuery = useOutbox('recent')
  const headsUpsQuery = useHeadsUps()
  const settings = useSettings()
  const actions = useOutboxActions()
  const { setCaptureOpen } = useWorkspace()
  const now = useUserNow(30_000)
  const { today, year } = userToday(now, settings.data?.timezone ?? 'Europe/Zurich')
  const headsUps = headsUpsQuery.data ?? []
  const pending = useMemo(() => pendingQuery.data?.entries ?? [], [pendingQuery.data])
  const recent = useMemo(() => {
    const waiting = new Set(pending.map((entry) => entry.id))
    return (recentQuery.data?.entries ?? []).filter(
      (entry) => entry.status !== 'pending' && !waiting.has(entry.id),
    )
  }, [recentQuery.data, pending])
  const writesEnabled = pendingQuery.data?.writesEnabled ?? recentQuery.data?.writesEnabled
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const lastIndex = useRef(0)
  const current = pending.find((entry) => entry.id === selectedId) ?? null

  // Keep a selection: after approving or discarding, the next entry is selected.
  useEffect(() => {
    if (pending.length === 0) {
      setSelectedId(null)
      return
    }
    const index = selectedId ? pending.findIndex((entry) => entry.id === selectedId) : -1
    if (index >= 0) {
      lastIndex.current = index
      return
    }
    setSelectedId(pending[Math.min(lastIndex.current, pending.length - 1)]?.id ?? null)
  }, [pending, selectedId])

  useEffect(() => {
    if (!selectedId) return
    document.querySelector(`[data-outbox-id="${selectedId}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [selectedId])

  useEffect(() => {
    const select = (entry: OutboxEntry | undefined) => {
      if (entry) setSelectedId(entry.id)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return
      if (isTypingTarget(event.target) || overlayOpen()) return
      const index = current ? pending.findIndex((entry) => entry.id === current.id) : -1
      const key = event.key
      if (key === 'j' || key === 'ArrowDown')
        select(pending[Math.min(index + 1, pending.length - 1)])
      else if (key === 'k' || key === 'ArrowUp') select(pending[Math.max(index - 1, 0)])
      else if (key === 'c') setCaptureOpen(true)
      else if (!current) return
      else if (key === 'a') void actions.approve(current, writesEnabled !== false)
      else if (key === 'x') void actions.discard(current)
      else return
      event.preventDefault()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [pending, current, actions, setCaptureOpen, writesEnabled])

  const loading = pendingQuery.isPending || recentQuery.isPending || headsUpsQuery.isPending
  const failure = pendingQuery.error ?? recentQuery.error
  const state = loading ? 'loading' : failure ? 'error' : 'ready'

  return (
    <View
      name="outbox"
      state={state}
      title="Outbox"
      description="Messages hugo would send for you. Nothing leaves hugo until you approve it."
    >
      {writesEnabled === false ? (
        <Alert role="status" data-testid="writes-off">
          <InfoIcon aria-hidden="true" />
          <AlertDescription className="text-foreground">
            Sending is off on this server. Approved messages are recorded as “Would have been sent”;
            nothing leaves hugo.
          </AlertDescription>
        </Alert>
      ) : null}

      {failure ? (
        <Alert variant="destructive">
          <AlertTitle>Could not load the outbox</AlertTitle>
          <AlertDescription>
            {failure instanceof ApiError
              ? failure.message
              : 'Something went wrong. Please try again.'}
          </AlertDescription>
        </Alert>
      ) : null}

      {headsUps.length > 0 ? (
        <section
          aria-labelledby="outbox-headsups-heading"
          className="flex flex-col gap-3"
          data-testid="headsups"
        >
          <h2 id="outbox-headsups-heading" className="text-sm font-medium text-muted-foreground">
            {toSendHeading(headsUps)}
          </h2>
          <ul className="divide-y overflow-hidden rounded-xl border">
            {headsUps.map((headsUp) => (
              <li key={headsUp.episode}>
                <HeadsUpCard headsUp={headsUp} today={today} thisYear={year} />
              </li>
            ))}
          </ul>
        </section>
      ) : headsUpsQuery.isError ? (
        <p className="text-sm text-destructive" data-testid="headsups-error">
          Could not load heads-ups.{' '}
          {headsUpsQuery.error instanceof ApiError ? headsUpsQuery.error.message : ''}
        </p>
      ) : null}

      <section
        aria-labelledby="outbox-pending-heading"
        className="flex flex-col gap-3"
        data-testid="outbox-pending"
      >
        <h2 id="outbox-pending-heading" className="text-sm font-medium text-muted-foreground">
          Waiting for approval
        </h2>
        {pendingQuery.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-40 w-full rounded-xl" />
            <Skeleton className="h-40 w-full rounded-xl" />
          </div>
        ) : pending.length === 0 ? (
          <Empty className="flex-none border border-dashed py-12" data-testid="empty-state">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <SendIcon aria-hidden="true" />
              </EmptyMedia>
              <EmptyTitle>Nothing waiting for approval</EmptyTitle>
              <EmptyDescription>
                Replies, heads-ups and reminders wait here until you approve them. Nothing leaves
                hugo without your OK.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <ul className="divide-y overflow-hidden rounded-xl border">
            {pending.map((entry) => (
              <li key={entry.id}>
                <OutboxItem
                  entry={entry}
                  now={now}
                  selected={current?.id === entry.id}
                  onSelect={(e) => setSelectedId(e.id)}
                  onApprove={(e) => void actions.approve(e, writesEnabled !== false)}
                  onDiscard={(e) => void actions.discard(e)}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      {recent.length > 0 ? (
        <section
          aria-labelledby="outbox-recent-heading"
          className="flex flex-col gap-3"
          data-testid="outbox-recent"
        >
          <h2 id="outbox-recent-heading" className="text-sm font-medium text-muted-foreground">
            Recent
          </h2>
          <ul className="divide-y overflow-hidden rounded-xl border">
            {recent.map((entry) => (
              <li key={entry.id}>
                <OutboxItem entry={entry} now={now} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </View>
  )
}
